import type { CostForwardingMarking } from "models/formTypes/util";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { CostForwarding, CostForwardingPut, CostForwardingState } from "models/utilTypes/costForwardingTypes";

export const COST_FORWARDING_DESCRIPTION_MAX_LENGTH = 512;

export type CostForwardingFormValues = CostForwardingMarking;

export type CostForwardingStatusLabel =
  | { key: "ReadyForForwarding"; customerName: string }
  | { key: "OnInvoiceDraft"; invoiceSerialNumber: number }
  | { key: "OnInvoiceDraftWithoutNumber" }
  | { key: "Forwarded"; invoiceSerialNumber: number }
  | { key: "ForwardedWithoutNumber" };

export interface CostForwardingStatus {
  label: CostForwardingStatusLabel;
}

// Any state past "ready" is treated as locked, so a state added on the server later stays read-only
// here instead of offering edits the server would refuse with AlreadyForwarded.
export const isCostForwardingLocked = (state?: number) =>
  state !== undefined && state !== CostForwardingState.ReadyForForwarding;

export function getCostForwardingFormValues(
  marking: CostForwarding | undefined,
  isAvailable: boolean,
  supplierName: string | undefined
): CostForwardingFormValues {
  if (!isAvailable) {
    return { costForwardingAvailable: false };
  }

  if (!marking) {
    return {
      costForwardingAvailable: true,
      costForwardingState: undefined,
      costForwardingCustomer: undefined,
      costForwardingDescription: (supplierName ?? "").slice(0, COST_FORWARDING_DESCRIPTION_MAX_LENGTH),
      costForwardingSavedCustomerId: undefined,
      costForwardingSavedDescription: undefined,
    };
  }

  return {
    costForwardingAvailable: true,
    costForwardingState: marking.state,
    costForwardingCustomer: { id: marking.customerId, value: marking.customerName ?? "" },
    costForwardingDescription: marking.description ?? "",
    costForwardingSavedCustomerId: marking.customerId,
    costForwardingSavedDescription: marking.description ?? "",
  };
}

// Core's HasLiveSourceDocument leaves these out of the costs to invoice, yet still accepts a marking on them.
// A deleted supplier invoice needs no check: the API does not serve it to the web app.
const deadReceiptStates: (WintSuperkollAPIModel.ReceiptStateApiModel | undefined)[] = [
  WintSuperkollAPIModel.ReceiptStateApiModel.Canceled,
];
const deadIncomingInvoiceStates: WintSuperkollAPIModel.IncomingInvoiceState[] = [
  WintSuperkollAPIModel.IncomingInvoiceState.Denied,
  WintSuperkollAPIModel.IncomingInvoiceState.DeniedAndHandled,
  WintSuperkollAPIModel.IncomingInvoiceState.DeniedAndUnhandled,
  WintSuperkollAPIModel.IncomingInvoiceState.SentBack,
];

/** Whether the receipt's cost can still reach a customer invoice; marking is offered only then. */
export const isForwardableReceipt = (receipt: Pick<WintSuperkollAPIModel.Receipt, "state"> | undefined) =>
  receipt !== undefined && !deadReceiptStates.includes(receipt.state);

/** Whether the supplier invoice's cost can still reach a customer invoice; marking is offered only then. */
export const isForwardableIncomingInvoice = (
  invoice: Pick<WintSuperkollAPIModel.IncomingInvoice, "state" | "isCredit"> | undefined
) =>
  // Core has no amount to forward for a credit invoice, so its cost would wait for booking forever.
  invoice !== undefined && !invoice.isCredit && !deadIncomingInvoiceStates.includes(invoice.state);

/**
 * The marking the document page shows. A ready marking on a document that can no longer be forwarded would
 * promise an invoice that never comes, but a cost already on a draft or an invoice is still worth showing.
 */
export const getShownCostForwardingMarking = (marking: CostForwarding | undefined, isForwardable: boolean) =>
  marking && (isForwardable || isCostForwardingLocked(marking.state)) ? marking : undefined;

export type CostForwardingMenuEntry = "mark" | "edit" | "release";

/**
 * Which entry the document page's more-actions menu offers. A cost already on an invoice can be detached
 * even once its document can no longer be forwarded.
 */
export function getCostForwardingMenuEntry(
  marking: CostForwarding | undefined,
  isAvailable: boolean,
  isForwardable: boolean,
  user: { isReadonlyUser: boolean; canUseInvoices: boolean }
): CostForwardingMenuEntry | undefined {
  if (!isAvailable || user.isReadonlyUser || !user.canUseInvoices) return undefined;
  if (!marking) return isForwardable ? "mark" : undefined;
  if (isCostForwardingLocked(marking.state)) return "release";
  return isForwardable ? "edit" : undefined;
}

// Cost forwarding is for users with invoicing rights only. The server refuses the others too; checking
// here as well keeps the step from appearing for them before that refusal arrives.
export const isCostForwardingOffered = (canUseInvoices: boolean, isAvailable: boolean, isForwardable: boolean) =>
  canUseInvoices && isAvailable && isForwardable;

/** The document whose marking to read; none without invoicing rights, since the server would refuse it. */
export const getCostForwardingReadId = (sourceId: number | undefined, canUseInvoices: boolean | undefined) =>
  canUseInvoices ? sourceId : undefined;

/** A new receipt has no marking to read, so invoicing rights alone decide whether the section shows. */
export const shouldOfferCostForwardingOnCreate = (isReceipt: boolean, canUseInvoices: boolean) =>
  isReceipt && canUseInvoices;

// The server cannot store a marking without a customer, so the customer alone decides whether the document
// is marked: choosing one marks it and clearing it un-marks it.
export const hasCostForwardingCustomer = (customer: CostForwardingFormValues["costForwardingCustomer"]) =>
  customer?.id !== undefined;

/** The PUT to send before the document itself is saved, or undefined when there is nothing to write. */
export function toCostForwardingPut(values: CostForwardingFormValues): CostForwardingPut | undefined {
  if (!values.costForwardingAvailable || isCostForwardingLocked(values.costForwardingState)) {
    return undefined;
  }

  const description = values.costForwardingDescription ?? "";

  if (!hasCostForwardingCustomer(values.costForwardingCustomer)) {
    return values.costForwardingState !== undefined ? { customerId: null, description } : undefined;
  }

  const customerId = Number(values.costForwardingCustomer?.id);
  const isUnchanged =
    values.costForwardingState !== undefined &&
    customerId === values.costForwardingSavedCustomerId &&
    description === values.costForwardingSavedDescription;

  return isUnchanged ? undefined : { customerId, description };
}

/**
 * The document page dialog's save. The dialog footer confirms any submit that resolves, including one that
 * sent nothing, so the confirmation is left to onSaved, which runs only after a marking was written.
 */
export async function saveMarkingFromDialog(
  values: CostForwardingFormValues,
  putMarking: (marking: CostForwardingPut) => Promise<unknown>,
  onSaved: (marking: CostForwardingPut) => void
): Promise<void> {
  const marking = toCostForwardingPut(values);
  if (!marking) return;

  await putMarking(marking);
  onSaved(marking);
}

export const isCostForwardingRemoval = (marking: CostForwardingPut) => marking.customerId === null;

/**
 * Writes the marking, then the document. A rejected marking rejects before the document is touched, so
 * the user can correct it and approve again instead of losing it on an already approved document.
 */
export async function saveMarkingBeforeDocument<T>(
  values: CostForwardingFormValues,
  putMarking: (marking: CostForwardingPut) => Promise<unknown>,
  saveDocument: () => Promise<T>,
  onMarkingSaved?: (marking: CostForwardingPut) => void
): Promise<T> {
  const marking = toCostForwardingPut(values);
  if (marking) {
    await putMarking(marking);
    onMarkingSaved?.(marking);
  }
  return saveDocument();
}

/**
 * Creates the document, then writes the marking against the id the create returned. A new receipt has
 * no id before it exists, so unlike saveMarkingBeforeDocument a rejected marking cannot stop the save:
 * the created document is kept and onMarkingFailed tells the user to mark it from its page.
 */
export async function saveMarkingAfterCreate<T>(
  values: CostForwardingFormValues,
  createDocument: () => Promise<T>,
  getCreatedId: (created: T) => number | undefined,
  putMarking: (sourceId: number, marking: CostForwardingPut) => Promise<unknown>,
  onMarkingFailed: () => void
): Promise<T> {
  const created = await createDocument();
  const marking = toCostForwardingPut(values);
  const createdId = getCreatedId(created);

  if (marking && createdId !== undefined) {
    await putMarking(createdId, marking).catch(() => onMarkingFailed());
  }
  return created;
}

/**
 * The form's view of the server after a successful PUT, so that a document save that then fails leaves
 * the form describing the marking the server now has.
 */
export function getCostForwardingValuesAfterSave(marking: CostForwardingPut): CostForwardingFormValues {
  return marking.customerId === null
    ? {
        costForwardingState: undefined,
        costForwardingSavedCustomerId: undefined,
        costForwardingSavedDescription: undefined,
      }
    : {
        costForwardingState: CostForwardingState.ReadyForForwarding,
        costForwardingSavedCustomerId: marking.customerId,
        costForwardingSavedDescription: marking.description,
      };
}

/**
 * The form's marking as the server holds it after a save. A receipt reloaded by the same save would otherwise get
 * the marking read before it, until the marking is read again.
 */
export function getCostForwardingValuesSaved(
  values: CostForwardingFormValues,
  marking: CostForwardingPut
): CostForwardingFormValues {
  const current = costForwardingFieldNames.reduce<CostForwardingFormValues>(
    (picked, name) => ({ ...picked, [name]: values[name] }),
    {}
  );
  return { ...current, ...getCostForwardingValuesAfterSave(marking) };
}

export function applyCostForwardingValues<T extends object>(values: T, costForwarding: CostForwardingFormValues): T {
  return { ...values, ...costForwarding };
}

export interface FormValuesHandle<T> {
  getValues?: () => T;
  setValues?: (values: T) => void;
}

/** Applies a setState-style update to a form that exposes its values only through its handle (GenericForm). */
export function updateFormHandleValues<T>(
  form: FormValuesHandle<T> | null | undefined,
  update: T | ((values: T) => T)
) {
  if (!form?.getValues || !form.setValues) return;

  form.setValues(typeof update === "function" ? (update as (values: T) => T)(form.getValues()) : update);
}

const costForwardingFieldNames: (keyof CostForwardingFormValues)[] = [
  "costForwardingAvailable",
  "costForwardingState",
  "costForwardingCustomer",
  "costForwardingDescription",
  "costForwardingSavedCustomerId",
  "costForwardingSavedDescription",
];

/** The fields "Visa all info" hands to the details page, all or nothing, so it writes the same marking. */
export function getCostForwardingValuesToCarry(values: CostForwardingFormValues): CostForwardingFormValues {
  if (!toCostForwardingPut(values)) return {};

  return costForwardingFieldNames.reduce<CostForwardingFormValues>(
    (carried, name) => ({ ...carried, [name]: values[name] }),
    {}
  );
}

/** The marking "Visa all info" handed to the details page in the route state, if it handed one over. */
export const getCarriedCostForwardingValues = (routeState: CostForwardingFormValues | undefined) =>
  routeState ? getCostForwardingValuesToCarry(routeState) : {};

const clearedCostForwardingValues = costForwardingFieldNames.reduce<CostForwardingFormValues>(
  (cleared, name) => ({ ...cleared, [name]: undefined }),
  {}
);

/**
 * The details form's values as its footer saves them. Once the document page has written the marking
 * itself, the marking carried over from the to-do is older than the server's and must not overwrite it.
 */
export function withoutCarriedCostForwarding<T extends object>(values: T, hasPageWrittenMarking: boolean): T {
  return hasPageWrittenMarking ? applyCostForwardingValues(values, clearedCostForwardingValues) : values;
}

export function getCostForwardingStatus(marking: CostForwarding): CostForwardingStatus {
  if (!isCostForwardingLocked(marking.state)) {
    return { label: { key: "ReadyForForwarding", customerName: marking.customerName ?? "" } };
  }

  // The server moves a marking out of "ready" as soon as the cost is saved onto a draft, so the
  // draft case must never be worded as forwarded.
  const serialNumber = marking.invoiceSerialNumber;
  const label: CostForwardingStatusLabel =
    marking.state === CostForwardingState.OnInvoiceDraft
      ? serialNumber !== undefined
        ? { key: "OnInvoiceDraft", invoiceSerialNumber: serialNumber }
        : { key: "OnInvoiceDraftWithoutNumber" }
      : serialNumber !== undefined
        ? { key: "Forwarded", invoiceSerialNumber: serialNumber }
        : { key: "ForwardedWithoutNumber" };

  return { label };
}

export type CostForwardingInvoiceLink = { invoiceId: number; key: "ViewInvoice" | "ViewInvoiceDraft" };

export function getCostForwardingInvoiceLink(
  marking: CostForwarding,
  canUseInvoices: boolean
): CostForwardingInvoiceLink | undefined {
  if (!canUseInvoices || marking.invoiceId === undefined || !isCostForwardingLocked(marking.state)) return undefined;
  return {
    invoiceId: marking.invoiceId,
    key: marking.state === CostForwardingState.OnInvoiceDraft ? "ViewInvoiceDraft" : "ViewInvoice",
  };
}

export type CostForwardingSectionSubText = { key: "Optional" } | { key: "Customer"; customerName: string };

export function getCostForwardingSectionSubText(marking: CostForwarding | undefined): CostForwardingSectionSubText {
  return marking ? { key: "Customer", customerName: marking.customerName ?? "" } : { key: "Optional" };
}
